import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AssetDetailSection from './AssetDetailSection';
import { GroupAccess } from '../../../api/group/groupAdminApiSlice';
import { Organization } from '../../../api/organization/organizationApiTypes';
import { makeOrganizationDetailUrl } from '../../../routes';

/**
 * Who may book this room (#1926) — READ-ONLY since option C
 * (docs/plans/coverage-per-room-assignment.md §4.2).
 *
 * Rooms are assigned on the organization (Kunden → Organisation → Räume &
 * Verträge), together with sole or shared use and the contract that covers
 * them. This section shows the result and links to each organization, where it
 * is changed; it no longer edits anything, so the object page's Save cannot
 * write access any more.
 *
 * A room is PUBLIC when no organization is assigned, or when the organizations
 * assigned to it only carry coverage (`restricted` false, "Öffentlich",
 * ADR-0008 amendment 2026-10-13): everyone can book it, and those
 * organizations' members are covered. Only a closed room is members-only.
 */
interface RoomAccessSectionProps {
    /** Persisted value; the section waits for it. */
    access?: GroupAccess;
    /** The tenant's customer organizations; the section hides when there are none. */
    organizations: Organization[];
}

const organizationLabel = (organization: Organization): string =>
    organization.displayName?.trim() || organization.legalName;

const RoomAccessSection: React.FC<RoomAccessSectionProps> = ({ access, organizations }) => {
    const { t } = useTranslation();

    if (!access) return null;
    // Decided from the room's OWN list, never from which organizations the
    // page happened to load (the list is paginated): a restricted room must
    // never read "Alle können buchen" because its organization was not on the
    // first page.
    const grantedIds = access.organizationIds;
    // An older deployment sends no `restricted`; then any grant closes the room.
    const closed = grantedIds.length > 0 && access.restricted !== false;
    const coveredOnOpen = !closed
        ? grantedIds.filter((id) => access.organizations?.find((o) => o.id === id)?.covered !== false)
        : [];
    if (grantedIds.length === 0 && organizations.length === 0) return null;
    const nameOf = (id: string): string => {
        const organization = organizations.find((candidate) => candidate.id === id);
        return organization ? organizationLabel(organization) : t('common.not_applicable');
    };

    return (
        <AssetDetailSection
            title={t('room_access.title')}
            subtitle={t('room_access.subtitle')}
        >
            <div className={'space-y-2 text-sm text-foreground'}>
                {!closed && <p>{t('room_access.view.everyone')}</p>}
                {coveredOnOpen.length > 0 && (
                    <>
                        <p>{t('room_access.view.covered_intro')}</p>
                        <ul className={'list-disc pl-5 space-y-1'}>
                            {coveredOnOpen.map((organizationId) => (
                                <li key={organizationId}>
                                    <Link
                                        to={makeOrganizationDetailUrl(organizationId)}
                                        className={'text-primary hover:underline'}
                                    >
                                        {nameOf(organizationId)}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                {closed && (
                    <>
                        <p>{t('room_access.view.restricted_intro')}</p>
                        <ul className={'list-disc pl-5 space-y-1'}>
                            {grantedIds.map((organizationId) => (
                                <li key={organizationId}>
                                    <Link
                                        to={makeOrganizationDetailUrl(organizationId)}
                                        className={'text-primary hover:underline'}
                                    >
                                        {nameOf(organizationId)}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                <p className={'text-foreground/60'}>{t('room_access.view.managed_on_organization')}</p>
            </div>
        </AssetDetailSection>
    );
};

export default RoomAccessSection;
